import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Workflow, Plus, Edit, Trash2, Play, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkflowsPage = () => {
  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: 'status_change',
    conditions: [{ field: 'status', operator: 'equals', value: '' }],
    actions: [{ type: 'assign_to', value: '' }]
  });

  useEffect(() => {
    fetchWorkflows();
    fetchUsers();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`, {
        withCredentials: true
      });
      setWorkflows(response.data.workflows || response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      toast.error('Failed to fetch workflows');
      setWorkflows([]);
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data.users || response.data || []);
    } catch (error) {
      console.error('Failed to fetch users');
      setUsers([]);
    }
  };

  const handleCreate = () => {
    setEditingWorkflow(null);
    setFormData({
      name: '',
      description: '',
      trigger: 'status_change',
      conditions: [{ field: 'status', operator: 'equals', value: '' }],
      actions: [{ type: 'assign_to', value: '' }]
    });
    setShowDialog(true);
  };

  const handleEdit = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      name: workflow.name,
      description: workflow.description || '',
      trigger: workflow.trigger,
      conditions: workflow.conditions,
      actions: workflow.actions
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingWorkflow) {
        await axios.put(
          `${API}/workflows/${editingWorkflow.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Workflow updated successfully');
      } else {
        await axios.post(
          `${API}/workflows`,
          formData,
          { withCredentials: true }
        );
        toast.success('Workflow created successfully');
      }
      
      setShowDialog(false);
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to save workflow');
    }
  };

  const handleToggle = async (workflowId) => {
    try {
      await axios.post(
        `${API}/workflows/${workflowId}/toggle`,
        {},
        { withCredentials: true }
      );
      toast.success('Workflow status updated');
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to toggle workflow');
    }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      await axios.delete(`${API}/workflows/${workflowId}`, {
        withCredentials: true
      });
      toast.success('Workflow deleted');
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const addCondition = () => {
    setFormData({
      ...formData,
      conditions: [...formData.conditions, { field: 'status', operator: 'equals', value: '' }]
    });
  };

  const removeCondition = (index) => {
    const newConditions = formData.conditions.filter((_, i) => i !== index);
    setFormData({ ...formData, conditions: newConditions });
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...formData.conditions];
    newConditions[index][field] = value;
    setFormData({ ...formData, conditions: newConditions });
  };

  const addAction = () => {
    setFormData({
      ...formData,
      actions: [...formData.actions, { type: 'assign_to', value: '' }]
    });
  };

  const removeAction = (index) => {
    const newActions = formData.actions.filter((_, i) => i !== index);
    setFormData({ ...formData, actions: newActions });
  };

  const updateAction = (index, field, value) => {
    const newActions = [...formData.actions];
    newActions[index][field] = value;
    setFormData({ ...formData, actions: newActions });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Workflow Automation</h1>
          <p className="text-slate-600 mt-2">Automate lead management with simple rules</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          New Workflow
        </Button>
      </div>

      {/* Help Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Workflow Examples
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
            <li>When lead status = "hot" → Assign to Sales Manager</li>
            <li>When lead stage = "qualified" → Create follow-up task</li>
            <li>When lead source = "website" → Change status to "new"</li>
            <li>When lead status = "won" → Add congratulations note</li>
          </ul>
        </CardContent>
      </Card>

      {/* Workflows Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workflows.map((workflow) => (
                <TableRow key={workflow.id}>
                  <TableCell className="font-medium">{workflow.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{workflow.trigger.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell>{workflow.conditions.length} condition(s)</TableCell>
                  <TableCell>{workflow.actions.length} action(s)</TableCell>
                  <TableCell>
                    <Switch
                      checked={workflow.is_active}
                      onCheckedChange={() => handleToggle(workflow.id)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(workflow)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(workflow.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {workflows.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Workflow className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No workflows yet. Create your first automation!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'New Workflow'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label>Workflow Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Auto-assign hot leads"
                />
              </div>
              
              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="What does this workflow do?"
                  rows={2}
                />
              </div>

              <div>
                <Label>Trigger</Label>
                <Select value={formData.trigger} onValueChange={(value) => setFormData({...formData, trigger: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status_change">When Status Changes</SelectItem>
                    <SelectItem value="stage_change">When Stage Changes</SelectItem>
                    <SelectItem value="lead_created">When Lead is Created</SelectItem>
                    <SelectItem value="field_updated">When Any Field Updates</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base">Conditions (All must match)</Label>
                <Button size="sm" variant="outline" onClick={addCondition}>Add Condition</Button>
              </div>
              {formData.conditions.map((condition, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Field</Label>
                    <Select value={condition.field} onValueChange={(value) => updateCondition(index, 'field', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="status">Status</SelectItem>
                        <SelectItem value="stage">Stage</SelectItem>
                        <SelectItem value="source">Source</SelectItem>
                        <SelectItem value="owner_id">Owner</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Operator</Label>
                    <Select value={condition.operator} onValueChange={(value) => updateCondition(index, 'operator', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equals">Equals</SelectItem>
                        <SelectItem value="not_equals">Not Equals</SelectItem>
                        <SelectItem value="contains">Contains</SelectItem>
                        <SelectItem value="is_empty">Is Empty</SelectItem>
                        <SelectItem value="is_not_empty">Is Not Empty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Value</Label>
                    <Input
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      placeholder="e.g., hot"
                    />
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeCondition(index)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base">Actions (Execute in order)</Label>
                <Button size="sm" variant="outline" onClick={addAction}>Add Action</Button>
              </div>
              {formData.actions.map((action, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Action Type</Label>
                    <Select value={action.type} onValueChange={(value) => updateAction(index, 'type', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="assign_to">Assign To User</SelectItem>
                        <SelectItem value="change_status">Change Status</SelectItem>
                        <SelectItem value="change_stage">Change Stage</SelectItem>
                        <SelectItem value="create_task">Create Task</SelectItem>
                        <SelectItem value="add_note">Add Note</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Value</Label>
                    {action.type === 'assign_to' ? (
                      <Select value={action.value} onValueChange={(value) => updateAction(index, 'value', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={typeof action.value === 'string' ? action.value : JSON.stringify(action.value)}
                        onChange={(e) => updateAction(index, 'value', e.target.value)}
                        placeholder={action.type === 'create_task' ? '{"title": "Follow up", "due_days": 3}' : 'Enter value'}
                      />
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeAction(index)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Workflow</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowsPage;
